import {useTranslation} from '@aboutyou/i18n';
import {Grow} from '@aboutyou/transitions';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC} from 'react';
import sc from 'styled-components';
import {COLORS} from '../../../../constants/colors';
import {Z_INDEXES} from '../../../../constants/layers';
import {SIZE, SPACING} from '../../../../constants/sizing';
import {
  ContentOpenerWrapper,
  OpenerType,
} from '../../../atoms/ContentPopper/ContentOpener';
import {Dropdown} from '../../../atoms/Dropdown/Dropdown';
import {Icon} from '../../../atoms/Icon/Icon';
import {IconName} from '../../../atoms/Icon/IconName';
import {NumberBadge} from '../../../atoms/NumberBadge/NumberBadge';
import {Text} from '../../../atoms/Text/Text';
import {ResetFiltersButton} from './ResetFiltersButton';

interface Props {
  label: string;
  displayIcon?: IconName;
  displayIconColor?: COLORS;
  activeCount?: number;
  dataTestId?: string;
  filterFooterContent?: React.ReactNode;
  filterContent: React.ReactNode;
  openerType?: OpenerType;
  padContent?: boolean;
  closeOnContentClick?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onReset?: () => void;
  className?: string;
  showScrollFade?: boolean;
  dropDownBodyRef?: React.RefObject<HTMLDivElement | null>;
  renderInPortal?: boolean;
}

export const FilterDropdown: FC<Props> = ({
  activeCount,
  dataTestId = 'filterDropdown',
  label,
  displayIcon,
  filterContent,
  filterFooterContent,
  openerType,
  closeOnContentClick,
  padContent = true,
  onOpen = () => null,
  onClose = () => null,
  onReset,
  className,
  showScrollFade,
  dropDownBodyRef,
  displayIconColor,
  renderInPortal = true,
}) => {
  const {t} = useTranslation();
  const [open, setOpen] = React.useState(false);
  const setClose = () => {
    setOpen(false);
    onClose();
  };

  const onOpenerClick = () => {
    setOpen(!open);
    onOpen();
  };

  return (
    <Wrapper data-testid={dataTestId}>
      <Dropdown
        open={open}
        className={className}
        isActive={Boolean(activeCount)}
        openerType={openerType}
        closeOnContentClick={closeOnContentClick}
        bodyElevation={Z_INDEXES.FILTER_DROPDOWN}
        disableFocusOnOpenerButton={true}
        openerContent={
          <OpenerContent size="px14">
            {displayIcon && (
              <DisplayIcon
                asset={displayIcon}
                fill={displayIconColor ?? COLORS.Black}
              />
            )}
            {label}
            <Grow
              mounted={Boolean(activeCount && activeCount > 0)}
              duration={200}
            >
              <Count
                active
                activeStyles={{
                  activeBg: COLORS.Black,
                  activeColor: COLORS.White,
                }}
                count={Number(activeCount)}
                size="small"
              />
            </Grow>
          </OpenerContent>
        }
        onOpenerClick={onOpenerClick}
        onClose={setClose}
        bodyContent={
          <div ref={dropDownBodyRef}>
            <DropdownContentWrapper
              data-testid="filterDropdownContent"
              data-show-fade={showScrollFade}
              $padContent={padContent}
            >
              <ScrollWrapper>{filterContent}</ScrollWrapper>
            </DropdownContentWrapper>

            <StickyContentWrapper>{filterFooterContent}</StickyContentWrapper>

            {onReset && (
              <ResetWrapper>
                <ResetFiltersButton
                  onReset={onReset}
                  dataTestId="resetButton"
                  label={t('Reset')}
                />
              </ResetWrapper>
            )}
          </div>
        }
        renderInPortal={renderInPortal}
      />
    </Wrapper>
  );
};

const Wrapper = linariaStyled.div`
  ${ContentOpenerWrapper} {
    height: ${SIZE.Xl};
  }
`;

const OpenerContent = sc(Text)`
  height: 100%;
  color: inherit;
  display: flex;
  align-items: center;
`;

export const DropdownContentWrapper = linariaStyled.div<{
  $padContent: boolean;
  $showFade?: boolean;
}>`
  box-sizing: border-box;
  overflow: hidden;
  padding: ${({$padContent}) =>
    $padContent ? `${SPACING['Y.Lg']} ${SPACING['X.Xl']}` : SPACING['Y.None']};
  position: relative;
  min-width: 360px;
  max-width: 400px;

  &[data-show-fade="true"] {
    padding-bottom: ${SPACING['Y.Sm']};

    &:after {
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0),
        rgb(255, 255, 255)
      );
      bottom: 0;
      content: '';
      display: block;
      height: 2.5rem;
      left: -0.5rem;
      pointer-events: none;
      position: absolute;
      right: -0.5rem;
    }
  }
`;

const ScrollWrapper = sc.div`
  box-sizing: border-box;
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.3rem 0;
`;

const StickyContentWrapper = sc.div`
  box-sizing: border-box;
`;

const StyledIcon = linariaStyled(Icon)`
  display: inline;
  cursor: pointer;
`;

const DisplayIcon = sc(StyledIcon)`
  height: ${SIZE.Lg};
  margin-right: ${SPACING['X.Sm']};
  width: ${SIZE.Lg};`;

const ResetWrapper = sc.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${COLORS.Grey3};
`;

const Count = sc(NumberBadge)`
  margin: ${SPACING['Y.None']};
  margin-left:${SPACING['X.Md']};
  min-width: unset;
`;
